import { modalIds } from "constants/modals-constants";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";
import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";
import { useProfile } from "hooks/useProfile";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import useRequest from "hooks/user/useRequest";

import { MainPageLayout } from "components/MainPageLayout";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { TeacherHeader } from "../Header/TeacherHeader";
import { TeacherNavigationMenu as OldTeacherNavigationMenu } from "../NavigationMenu/TeacherNavigationMenu";
import { TeacherNavigationMenu } from "../NavigationMenuV2/TeacherNavigationMenu";

interface TeacherPageLayoutProps {
  children: React.ReactNode;
  withMenu?: boolean;
}

export const TeacherPageLayout: React.FC<TeacherPageLayoutProps> = observer(
  ({ children, withMenu }) => {
    const history = useHistory();
    const { toggleModal } = useModal();
    const { logout } = useRequest();
    const { fetchProducts } = useProduct();
    const { fetchUserDetails, userDetails, updateUserDetails } = useProfile();
    const selectedProductId = useSelectedProductId();
    const { enableUxImprovements } = useFeatureFlags();

    useEffect(() => {
      fetchProducts();
      fetchUserDetails();
    }, []);

    const onToggleSideMenu = () => {
      toggleModal(modalIds.modalSideMenu);
    };

    const navigateToStudentEnvironment = () => {
      if (selectedProductId) {
        return history.push(
          buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS, {
            productId: selectedProductId,
          }),
        );
      }
    };

    return (
      <MainPageLayout
        header={
          <TeacherHeader
            navigateToStudentEnvironment={navigateToStudentEnvironment}
            toggleSideMenu={onToggleSideMenu}
            updateUserDetails={updateUserDetails}
            userDetails={userDetails}
            onLogout={logout}
          />
        }
        navigationMenu={
          enableUxImprovements ? (
            <TeacherNavigationMenu
              className="aside-menu"
              userDetails={userDetails}
              shouldShowSideNav={!!withMenu}
              dataCy="product-navigation-menu"
              toggleSideMenu={onToggleSideMenu}
              updateUserDetails={updateUserDetails}
              onLogout={logout}
              navigateToStudentEnvironment={navigateToStudentEnvironment}
            />
          ) : (
            <OldTeacherNavigationMenu className="aside-menu" userDetails={userDetails} />
          )
        }
        shouldShowHeader={!enableUxImprovements}
        shouldShowSideNav={!!withMenu}
      >
        {children}
      </MainPageLayout>
    );
  },
);
